// Idle micro-motion helpers. Low-amplitude pseudo-noise for breathing,
// head sway, ear flicks, tail-tip wander — the difference between a
// still character and a still *living* character.
//
// Pure functions of (t, seed). Sum-of-sines is enough for organic
// wander; true Perlin would be overkill.

/**
 * Sum of three sine waves at incommensurable frequencies. Output is
 * bounded in [-1, 1] and visually wander-like at any t. Use the seed
 * to give different bones (or different squirrels) independent
 * trajectories from the same time base.
 */
export function sumSines(
  t: number,
  seed: number,
  freqs: readonly number[] = [0.7, 1.13, 1.87],
): number {
  let total = 0;
  for (let i = 0; i < freqs.length; i++) {
    total += Math.sin(t * freqs[i] * Math.PI * 2 + seed * (i + 1) * 1.234);
  }
  return total / freqs.length;
}

/**
 * Step-and-smooth random twitch. Value holds a deterministic random
 * level for `period` seconds, then smoothly transitions to a new one.
 * For ear flicks and other intentional-looking micro-motions.
 */
export function twitch(t: number, seed: number, period: number): number {
  const slot = Math.floor(t / period);
  const localT = t / period - slot;
  const a = pseudoRandom(slot, seed);
  const b = pseudoRandom(slot + 1, seed);
  const window = 0.2;
  if (localT > 1 - window) {
    const u = (localT - (1 - window)) / window;
    const s = u * u * (3 - 2 * u);
    return a * (1 - s) + b * s;
  }
  return a;
}

/**
 * Breathing oscillator. Returns 0..1; one cycle every 1/ratePerSec
 * seconds. Use to scale breath-driven motion (chest rise, slight
 * vertical body bob, ribcage scale).
 */
export function breath(t: number, ratePerSec: number, seed: number = 0): number {
  return (Math.sin(t * ratePerSec * Math.PI * 2 + seed) + 1) / 2;
}

function pseudoRandom(n: number, seed: number): number {
  // Tiny xorshift-ish hash; deterministic per (n, seed). Returns [-1, 1].
  let x = (n * 374761393 + seed * 668265263) | 0;
  x = (x ^ (x >>> 13)) | 0;
  x = Math.imul(x, 1274126177);
  x = (x ^ (x >>> 16)) | 0;
  return ((x & 0x7fffffff) / 0x7fffffff) * 2 - 1;
}
